import { Language } from '@libraries/design-system';
import { constructAddressObjToString } from '@libraries/helpers';
import { PropertyType } from '@libraries/manager-client';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { PreferredLanguage } from '../models';
import { PrequalState } from '../reducers/prequal.reducer';
import { selectCommercialQueryParam, selectRouterPrimaryOutlet } from './router.selectors';

export const selectPrequalState = createFeatureSelector<PrequalState>('prequal');

// Loading & Error State Selectors //

export const isLoading = createSelector(selectPrequalState, (prequal) => prequal.loading);

export const isLeftOutletLoading = createSelector(
  selectPrequalState,
  selectRouterPrimaryOutlet,
  (prequal, outlet) => {
    return prequal.loading && outlet === 'left';
  }
);

export const isRightOutletLoading = createSelector(
  selectPrequalState,
  selectRouterPrimaryOutlet,
  (prequal, outlet) => {
    return prequal.loading && outlet === 'right';
  }
);

export const isError = createSelector(selectPrequalState, (prequal) => prequal.error);

export const getErrorMessage = createSelector(
  selectPrequalState,
  (prequal) => prequal.errorMessage
);

export const getAddressError = createSelector(getErrorMessage, (errorMessage) => {
  return errorMessage.includes('address') ? errorMessage : null;
});

// UI Selectors //

export const isDesktopActive = createSelector(
  selectPrequalState,
  (prequal) => prequal.screenActive === 'Desktop'
);

export const isMainActive = createSelector(
  selectPrequalState,
  (prequal) => prequal.screenActive === 'Main'
);

// State Data Selectors //

export const getAddressChoices = createSelector(selectPrequalState, (prequal) =>
  prequal.addressChoices.map((choice) => ({
    ...choice,
    display: constructAddressObjToString(choice),
  }))
);

export const getAddress = createSelector(selectPrequalState, (prequal) => prequal.address);

export const getPreferredLanguage = createSelector(
  selectPrequalState,
  (prequal) => prequal.preferredLanguage
);

export const preferredLanguageIsSpanish = createSelector(
  getPreferredLanguage,
  (language) => language === Language.Espanol
);

export const spanishWorkflowEnabled = createSelector(
  selectPrequalState,
  (prequal) => prequal.spanishWorkflowEnabled
);

export const isSkipApplicationAllowed = createSelector(
  selectPrequalState,
  (prequal) => prequal.skipApplicationAllowed
);

export const spanishDocsEnabled = createSelector(
  selectPrequalState,
  (prequal) => prequal.spanishDocsEnabled
);

export const spanishProgramGuidelinesLink = createSelector(
  selectPrequalState,
  preferredLanguageIsSpanish,
  spanishDocsEnabled,
  (state, preferredLanguageSpanish, isSpanishDocsEnabled) =>
    preferredLanguageSpanish && isSpanishDocsEnabled
      ? state.programHandbookLinks.spanish
      : state.programHandbookLinks.english
);

export const getAddressWithoutZip = createSelector(getAddress, (address) =>
  address.replace(/\d+$/, '')
);

export const getEmail = createSelector(selectPrequalState, (prequal) => prequal.email);

export const getPhone = createSelector(selectPrequalState, (prequal) => prequal.phone);

export const getToken = createSelector(selectPrequalState, (prequal) => prequal.token);

export const getSelectedImprovement = createSelector(
  selectPrequalState,
  (prequal) => prequal.improvementList[0]
);

export const hasMonthlyCosts = createSelector(
  selectPrequalState,
  (prequal) => !!prequal.monthlyCosts
);

export const getMonthlyCosts = createSelector(
  selectPrequalState,
  (prequal) => prequal.monthlyCosts
);

export const getImprovementCost = createSelector(
  selectPrequalState,
  (prequal) => prequal.estimatedCost
);

export const showUnsecuredPage = createSelector(
  selectPrequalState,
  preferredLanguageIsSpanish,
  (prequal, isSpanish) => prequal.propertyType === PropertyType.Residential && !isSpanish
);

// Guard Selectors //

export const hasAddressChoices = createSelector(
  selectPrequalState,
  (prequal) => !!(prequal.addressChoices && prequal.addressChoices.length)
);

export const hasAddress = createSelector(selectPrequalState, (prequal) => !!prequal.address);

export const hasToken = createSelector(selectPrequalState, (prequal) => !!prequal.token);

// Data Aggregator Selectors //

export const getComingSoonPrefill = createSelector(selectPrequalState, (prequal) => ({
  note: {
    name: '',
    message: '',
    phone: prequal.phone || '',
    address: prequal.address || '',
  },
  otherDetails: '',
  propertyType: 'Residential',
  improvements: prequal.improvementList,
}));

export const getQuestionsPrefill = createSelector(selectPrequalState, (prequal) => ({
  baseQuestions: {
    address: prequal.address || '',
    phone: prequal.phone || '',
    email: prequal.email || '',
  },
  improvements: prequal.improvementList,
  timeframes: prequal.urgency || '',
}));

export const getIneligiblePropertyData = createSelector(selectPrequalState, (prequal) => ({
  address: prequal.address,
  email: prequal.email,
  boundaryCode: prequal.boundaryCode,
}));

export const getUser = createSelector(selectPrequalState, (prequal) => ({
  email: prequal.email,
  phone: prequal.phone,
  email_communication: prequal.email_communication,
}));

export const getMetadata = createSelector(selectPrequalState, (prequal) => ({
  how_soon: prequal.urgency,
  improvements: prequal.improvementList,
}));

// refers to images in libs/assets/src/lib/image-registry/image-registry.ts
export const getEligibilityImage = createSelector(selectCommercialQueryParam, (commercial) =>
  commercial ? 'commercialEligibility' : 'eligibility'
);

// Payload Builder Selectors //

export const spanishDocsEnabledDisclaimer = createSelector(
  spanishWorkflowEnabled,
  preferredLanguageIsSpanish,
  spanishDocsEnabled,
  (workflowEnabled, preferredSpanish, docsEnabled) =>
    !workflowEnabled && preferredSpanish && docsEnabled
);

export const spanishDocsDisabledDisclaimer = createSelector(
  spanishWorkflowEnabled,
  preferredLanguageIsSpanish,
  spanishDocsEnabled,
  (workflowEnabled, preferredSpanish, docsEnabled) =>
    !workflowEnabled && preferredSpanish && !docsEnabled
);

export const getAddressSelectPayload = createSelector(getUser, getMetadata, (user, metadata) => ({
  metadata,
  user,
}));

export const formatPreferredLanguage = createSelector(getPreferredLanguage, (lang: Language) =>
  lang === Language.Espanol ? PreferredLanguage.Espanol : PreferredLanguage.English
);
